import { MessageSquare } from 'lucide-react';
import { useMemo, useState } from 'react';

import { StarRating } from 'components/StarRating';
import {
  type ReviewSort,
  useTutorReviewStats,
  useTutorReviews,
} from 'hooks/useTutorReviews';
import { Select } from 'ui';

import classes from './TutorReviewsSection.module.scss';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

const SELECT_OPTION_PROPS_MAP = {
  key: 'value',
  title: 'label',
  viewValue: 'label',
} as const;

const formatReviewDate = (value: string) => {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return value;
  }
};

const SORT_OPTIONS: { value: ReviewSort; label: string }[] = [
  { value: 'newest', label: 'Сначала новые' },
  { value: 'rating_desc', label: 'Сначала лучшие' },
  { value: 'rating_asc', label: 'Сначала худшие' },
];

type TutorReviewsSectionProps = {
  tutorUserId: number;
  publicAccess?: boolean;
  showPendingBadge?: boolean;
};

export const TutorReviewsSection = ({
  tutorUserId,
  publicAccess = false,
  showPendingBadge = false,
}: TutorReviewsSectionProps) => {
  const [sort, setSort] = useState<ReviewSort>('newest');
  const [page, setPage] = useState(0);
  const limit = 5;
  const offset = page * limit;

  const { data: stats } = useTutorReviewStats(tutorUserId, publicAccess);
  const { data, isLoading, isError } = useTutorReviews({
    tutorUserId,
    sort,
    limit,
    offset,
    publicAccess,
  });

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data?.total ?? 0) / limit)),
    [data?.total, limit],
  );

  // Figma 3:30077: пустое состояние - только заголовок и центрированная заглушка с иконкой.
  const isEmpty =
    !isLoading && !isError && (data?.items.length ?? 0) === 0 && (stats?.total_reviews ?? 0) === 0;

  const scrollToReviews = () => {
    document.getElementById('tutor-reviews')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className={classes.section} id='tutor-reviews'>
      <div className={classes.header}>
        <div>
          <h2>Отзывы</h2>
          {!isEmpty && (stats?.total_reviews ?? 0) > 0 ? (
            <p className={classes.summary}>
              {`${stats?.total_reviews} ${stats?.total_reviews === 1 ? 'отзыв' : 'отзывов'}`}
            </p>
          ) : null}
        </div>
        {!publicAccess && !isEmpty ? (
          <button type='button' className={classes.linkBtn} onClick={scrollToReviews}>
            Все отзывы
          </button>
        ) : null}
      </div>

      {isEmpty ? null : (
      <div className={classes.controls}>
        <Select
          shape='brick'
          size='md'
          variant='outlined'
          label='Сортировка'
          placeholder='Выберите'
          options={SORT_OPTIONS}
          optionPropsMap={SELECT_OPTION_PROPS_MAP}
          value={sort}
          onChange={(value) => {
            setSort((value as ReviewSort) || 'newest');
            setPage(0);
          }}
          createPortal
        />
      </div>
      )}

      {isLoading ? <BrandLoader center label='Загружаем отзывы...' /> : null}
      {isError ? <p>Не удалось загрузить отзывы.</p> : null}

      {!isLoading && !isError && (data?.items.length ?? 0) === 0 ? (
        <div className={classes.emptyState}>
          <MessageSquare className={classes.emptyIcon} aria-hidden='true' />
          <p className={classes.emptyTitle}>Пока нет отзывов</p>
          <p className={classes.emptyText}>Здесь появятся отзывы учеников после занятий.</p>
        </div>
      ) : null}

      <ul className={classes.list}>
        {data?.items.map((item) => (
          <li key={item.id} className={classes.item}>
            <div className={classes.itemHead}>
              <strong>{item.student_name}</strong>
              <StarRating value={item.rating} size='sm' />
            </div>
            {showPendingBadge && !item.verified ? (
              <span className={classes.pending}>
                На проверке. Отзыв еще не опубликован.
              </span>
            ) : null}
            {item.comment ? <p>{item.comment}</p> : null}
            <time>{formatReviewDate(item.date_create)}</time>
          </li>
        ))}
      </ul>

      {totalPages > 1 ? (
        <nav className={classes.pagination} aria-label='Страницы отзывов'>
          <button type='button' disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Назад
          </button>
          <span>
            {page + 1} / {totalPages}
          </span>
          <button
            type='button'
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Вперед
          </button>
        </nav>
      ) : null}
    </section>
  );
};
